/**
 * On tornar després d'entrar, quan la sessió ha caducat a mig fer servir l'app.
 *
 * Es desa a `sessionStorage` (i no a l'estat del router) perquè el camí habitual cap a
 * `/login` és una recàrrega sencera (`window.location.href`), que perd l'estat de React.
 * Només es desa per sessió caducada: un «tancar sessió» voluntari l'esborra, perquè qui
 * entri després no aterri a la pantalla de qui hi era.
 */
import { isPublicRoute } from '@/lib/constants/public-routes';

const RETURN_TO_KEY = 'auth:returnTo';

/**
 * Només camins interns de l'app: comença amb una sola `/`, sense `//` ni `\` (que un
 * navegador pot llegir com un altre domini), i mai una ruta pública (`/login`, formularis).
 */
export function sanitizeReturnTo(path: unknown): string | null {
  if (typeof path !== 'string' || path.length === 0 || path.length > 2000) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return null;
  const pathname = path.split(/[?#]/)[0];
  if (isPublicRoute(pathname)) return null;
  return path;
}

function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Desa el camí si és vàlid; si no ho és, no toca el que ja hi hagués. */
export function saveReturnTo(path: unknown): void {
  const safe = sanitizeReturnTo(path);
  if (!safe) return;
  try {
    storage()?.setItem(RETURN_TO_KEY, safe);
  } catch {
    // Emmagatzematge ple o blocat: s'entrarà pel tauler, com sempre.
  }
}

/** Llegeix el camí desat i l'esborra: només serveix per a un login. */
export function consumeReturnTo(): string | null {
  try {
    const store = storage();
    if (!store) return null;
    const value = store.getItem(RETURN_TO_KEY);
    store.removeItem(RETURN_TO_KEY);
    return sanitizeReturnTo(value);
  } catch {
    return null;
  }
}

export function clearReturnTo(): void {
  try {
    storage()?.removeItem(RETURN_TO_KEY);
  } catch {
    // res a fer
  }
}

/** On anar després d'entrar: on s'era quan la sessió va caducar o, si no, el tauler. */
export const DEFAULT_POST_LOGIN_PATH = '/dashboard';

export function postLoginPath(): string {
  return consumeReturnTo() ?? DEFAULT_POST_LOGIN_PATH;
}

/** El camí actual del navegador (amb cerca i àncora), per desar-lo abans d'anar a `/login`. */
export function currentPath(): string | null {
  if (typeof window === 'undefined') return null;
  const { pathname, search, hash } = window.location;
  return `${pathname}${search ?? ''}${hash ?? ''}`;
}
